import { createContext, useContext } from 'react';

/**
 * =============================================================================
 * SHAPE-AWARE LIST CONTEXT
 * =============================================================================
 *
 * Provides geometry information about the container cutout to child components
 * so they can dynamically calculate clip-paths that morph around the dock.
 *
 * =============================================================================
 */

/**
 * Panel side - which panel the list is in (Notes = left, Gauge = right)
 */
export type PanelSide = 'left' | 'right';

/**
 * Cutout geometry - all measurements needed to calculate clip-paths
 */
export interface CutoutGeometry {
  /** Y coordinate where the cutout begins (top edge, in container coords) */
  shelfY: number;
  /** Y coordinate where the cutout ends (bottom edge, in container coords) */
  floorY: number;
  /** X coordinate of the cutout's left edge (for left panel items) */
  leftEdge: number;
  /** X coordinate of the cutout's right edge (for right panel items) */
  rightEdge: number;
  /** Radius of the arcs wrapping the dock corners */
  wrapRadius: number;
  /** Scaled corner radius of the dock itself */
  scaledCornerRadius: number;
  /** X coordinate of the dock's left side */
  dockLeft: number;
  /** X coordinate of the dock's right side */
  dockRight: number;
  /** X coordinate of the dock's center */
  dockCenterX: number;
  /** Y coordinate of the dock's top edge */
  dockTop: number;
  /** Y coordinate of the dock's bottom edge */
  dockBottom: number;
  /** Total height of the container */
  containerHeight: number;
  /** Total width of the container */
  containerWidth: number;
}

/**
 * Shape context value - provided to all child components
 */
export interface ShapeAwareListContextValue {
  /** The panel side (left = Notes, right = Gauge) */
  panelSide: PanelSide;
  /** Current cutout geometry (null if not yet measured) */
  geometry: CutoutGeometry | null;
  /** Whether the dock is currently hovered */
  isDockHovered: boolean;
  /** Uniform padding from container edges */
  edgePadding: number;
  /** Reference to the scroll container element */
  scrollContainerRef: React.RefObject<HTMLElement> | null;
}

/**
 * Default context value
 */
const defaultContextValue: ShapeAwareListContextValue = {
  panelSide: 'left',
  geometry: null,
  isDockHovered: false,
  edgePadding: 10,
  scrollContainerRef: null,
};

/**
 * React context for shape-aware list
 */
export const ShapeAwareListContext = createContext<ShapeAwareListContextValue>(defaultContextValue);

/**
 * Hook to consume shape-aware list context
 */
export function useShapeAwareList(): ShapeAwareListContextValue {
  return useContext(ShapeAwareListContext);
}
